/**
 * AD Shortest Paths queries - attack path analysis for privilege escalation.
 */

import { QueryFolders } from '@/sections/insights/queryBuilder/savedQueries/folders';
import type { SavedQuery } from '@/sections/insights/queryBuilder/types/SavedQuery';

import {
  ACL_OWNERSHIP_RELATIONSHIPS,
  AD_PATHFINDING_RELATIONSHIPS,
  RDP_ACCESS_RELATIONSHIPS,
} from './relationships';

export const AD_SHORTEST_PATHS_QUERIES: SavedQuery[] = [
  {
    id: 'shortest-paths-to-domain-admins-from-computers',
    name: 'Shortest Paths to Domain Admins from Computers',
    type: 'graph',
    description:
      'Find the shortest attack paths from any computer to the Domain Admins group. Critical for identifying privilege escalation routes through compromised machines.',
    folderId: QueryFolders.AD_ATTACK_PATHS,
    query: {
      node: {
        labels: ['ADComputer'],
        filters: [],
        relationships: [
          {
            label: [...AD_PATHFINDING_RELATIONSHIPS],
            optional: false,
            length: 5,
            target: {
              labels: ['ADGroup'],
              filters: [
                {
                  field: 'objectid',
                  operator: 'ENDS WITH',
                  value: '-512',
                  not: false,
                },
              ],
              relationships: [],
            },
          },
        ],
      },
      limit: 200,
      shortest: 1,
    },
  },
  {
    id: 'shortest-paths-from-domain-users-to-domain-admins',
    name: 'Shortest Paths from Domain Users to Domain Admins',
    type: 'graph',
    description:
      'Identify attack paths from the Domain Users group to Domain Admins. Shows how a standard user account could escalate to full domain control.',
    folderId: QueryFolders.AD_ATTACK_PATHS,
    query: {
      node: {
        labels: ['ADGroup'],
        filters: [
          {
            field: 'objectid',
            operator: 'ENDS WITH',
            value: '-513',
            not: false,
          },
        ],
        relationships: [
          {
            label: [...AD_PATHFINDING_RELATIONSHIPS],
            optional: false,
            length: 5,
            target: {
              labels: ['ADGroup'],
              filters: [
                {
                  field: 'objectid',
                  operator: 'ENDS WITH',
                  value: '-512',
                  not: false,
                },
              ],
              relationships: [],
            },
          },
        ],
      },
      limit: 200,
      shortest: 1,
    },
  },
  {
    id: 'paths-from-non-privileged-groups-to-domain-admins',
    name: 'Paths from Non-Privileged Groups to Domain Admins',
    type: 'graph',
    description:
      'Find attack paths from groups without admin privileges (admincount=false) to Domain Admins. Reveals unexpected privilege escalation routes.',
    folderId: QueryFolders.AD_ATTACK_PATHS,
    query: {
      node: {
        labels: ['ADGroup'],
        filters: [
          { field: 'admincount', operator: '=', value: true, not: true },
        ],
        relationships: [
          {
            label: [...AD_PATHFINDING_RELATIONSHIPS],
            optional: false,
            length: 5,
            target: {
              labels: ['ADGroup'],
              filters: [
                {
                  field: 'objectid',
                  operator: 'ENDS WITH',
                  value: '-512',
                  not: false,
                },
              ],
              relationships: [],
            },
          },
        ],
      },
      limit: 200,
      shortest: 1,
    },
  },
  {
    id: 'all-edges-from-unprivileged-users',
    name: 'All Edges from Unprivileged Users',
    type: 'graph',
    description:
      'Map all attack vectors available to non-admin users (admincount=false). Comprehensive view of standard user capabilities.',
    folderId: QueryFolders.AD_ATTACK_PATHS,
    query: {
      node: {
        labels: ['ADUser'],
        filters: [
          { field: 'admincount', operator: '=', value: true, not: true },
        ],
        relationships: [
          {
            label: [...AD_PATHFINDING_RELATIONSHIPS],
            optional: false,
            length: 5,
            target: {
              labels: ['ADObject'],
              filters: [],
              relationships: [],
            },
          },
        ],
      },
      limit: 200,
      shortest: 1,
    },
  },
  {
    id: 'rdp-paths-to-tier0',
    name: 'RDP Exposure to Tier 0 Assets',
    type: 'graph',
    description:
      'Find standard users with RDP-style access into domain controllers and other Tier 0 systems.',
    folderId: QueryFolders.AD_ATTACK_PATHS,
    query: {
      node: {
        labels: ['ADUser'],
        filters: [
          { field: 'admincount', operator: '=', value: true, not: true },
        ],
        relationships: [
          {
            label: [...RDP_ACCESS_RELATIONSHIPS],
            optional: false,
            length: 2,
            target: {
              labels: ['ADComputer'],
              filters: [
                { field: 'isdc', operator: '=', value: true, not: false },
              ],
              relationships: [],
            },
          },
        ],
      },
      limit: 200,
      shortest: 1,
    },
  },
  {
    id: 'acl-ownership-drift',
    name: 'ACL Ownership Drift',
    type: 'graph',
    description:
      'Find non-admin users or groups that hold powerful ACL rights over protected objects.',
    folderId: QueryFolders.AD_ATTACK_PATHS,
    query: {
      node: {
        labels: ['ADObject'],
        filters: [
          { field: 'admincount', operator: '=', value: true, not: true },
          {
            field: '',
            operator: 'OR',
            value: [
              { field: 'class', operator: '=', value: 'user' },
              { field: 'class', operator: '=', value: 'group' },
            ],
          },
        ],
        relationships: [
          {
            label: [...ACL_OWNERSHIP_RELATIONSHIPS],
            optional: false,
            length: 5,
            target: {
              labels: ['ADObject'],
              filters: [
                { field: 'admincount', operator: '=', value: true, not: false },
              ],
              relationships: [],
            },
          },
        ],
      },
      limit: 200,
      shortest: 1,
    },
  },
  {
    id: 'shortest-paths-kerberoastable-to-domain-admins',
    name: 'Shortest Paths from Kerberoastable Users to Domain Admins',
    type: 'graph',
    description:
      'Find attack paths from Kerberoastable users to Domain Admins. Shows risk if Kerberoasting succeeds.',
    folderId: QueryFolders.AD_ATTACK_PATHS,
    query: {
      node: {
        labels: ['ADUser'],
        filters: [
          { field: 'hasspn', operator: '=', value: true, not: false },
          {
            field: 'objectid',
            operator: 'ENDS WITH',
            value: '-502',
            not: true,
          },
        ],
        relationships: [
          {
            label: [...AD_PATHFINDING_RELATIONSHIPS],
            optional: false,
            length: 5,
            target: {
              labels: ['ADGroup'],
              filters: [
                {
                  field: 'objectid',
                  operator: 'ENDS WITH',
                  value: '-512',
                  not: false,
                },
              ],
              relationships: [],
            },
          },
        ],
      },
      limit: 200,
      shortest: 1,
    },
  },
  {
    id: 'shortest-paths-to-unconstrained-delegation',
    name: 'Shortest Paths to Unconstrained Delegation Systems',
    type: 'graph',
    description:
      'Find attack paths to computers with unconstrained delegation. These can capture TGTs for domain compromise.',
    folderId: QueryFolders.AD_ATTACK_PATHS,
    query: {
      node: {
        labels: ['ADUser'],
        filters: [
          { field: 'admincount', operator: '=', value: true, not: true },
        ],
        relationships: [
          {
            label: [...AD_PATHFINDING_RELATIONSHIPS],
            optional: false,
            length: 5,
            target: {
              labels: ['ADComputer'],
              filters: [
                {
                  field: 'unconstraineddelegation',
                  operator: '=',
                  value: true,
                  not: false,
                },
              ],
              relationships: [],
            },
          },
        ],
      },
      limit: 200,
      shortest: 1,
    },
  },
  {
    id: 'shortest-paths-to-tier-zero',
    name: 'Shortest Paths to Tier Zero / High Value Targets',
    type: 'graph',
    description:
      'Find attack paths to Tier Zero assets including Domain Controllers, Domain Admins, and other high-value targets.',
    folderId: QueryFolders.AD_ATTACK_PATHS,
    query: {
      node: {
        labels: ['ADObject'],
        filters: [],
        relationships: [
          {
            label: [...AD_PATHFINDING_RELATIONSHIPS],
            optional: false,
            length: 5,
            target: {
              labels: ['ADObject'],
              filters: [
                {
                  field: 'tags',
                  operator: 'IN',
                  value: 'tier-zero',
                  reverseOperands: true,
                  not: false,
                },
              ],
              relationships: [],
            },
          },
        ],
      },
      limit: 200,
      shortest: 1,
    },
  },
  {
    id: 'paths-domain-users-to-tier-zero',
    name: 'Paths from Domain Users to Tier Zero / High Value Targets',
    type: 'graph',
    description:
      'Find attack paths from the Domain Users group to Tier Zero targets. Shows baseline privilege escalation risk.',
    folderId: QueryFolders.AD_ATTACK_PATHS,
    query: {
      node: {
        labels: ['ADGroup'],
        filters: [
          {
            field: 'objectid',
            operator: 'ENDS WITH',
            value: '-513',
            not: false,
          },
        ],
        relationships: [
          {
            label: [...AD_PATHFINDING_RELATIONSHIPS],
            optional: false,
            length: 5,
            target: {
              labels: ['ADObject'],
              filters: [
                {
                  field: 'tags',
                  operator: 'IN',
                  value: 'tier-zero',
                  reverseOperands: true,
                  not: false,
                },
              ],
              relationships: [],
            },
          },
        ],
      },
      limit: 200,
      shortest: 1,
    },
  },
  {
    id: 'shortest-paths-to-domain-admins',
    name: 'Shortest Paths to Domain Admins',
    type: 'graph',
    description:
      'Find shortest attack paths from any principal to the Domain Admins group.',
    folderId: QueryFolders.AD_ATTACK_PATHS,
    query: {
      node: {
        labels: ['ADObject'],
        filters: [],
        relationships: [
          {
            label: [...AD_PATHFINDING_RELATIONSHIPS],
            optional: false,
            length: 5,
            target: {
              labels: ['ADGroup'],
              filters: [
                {
                  field: 'objectid',
                  operator: 'ENDS WITH',
                  value: '-512',
                  not: false,
                },
              ],
              relationships: [],
            },
          },
        ],
      },
      limit: 200,
      shortest: 1,
    },
  },
];
